import type { ICityConfig } from './types';
import type { StaticSuggestions } from '~/types/address-context';
import type { Addresses } from '~~/core/entities/Addresses';

export class CityDataRegistry {
	private static instance: CityDataRegistry;
	private cities: Map<string, ICityConfig> = new Map();
	private defaultCity: ICityConfig | null = null;

	private constructor() {}

	static getInstance(): CityDataRegistry {
		if (!CityDataRegistry.instance) {
			CityDataRegistry.instance = new CityDataRegistry();
		}
		return CityDataRegistry.instance;
	}

	registerCity(cityConfig: ICityConfig): void {
		this.cities.set(cityConfig.id, cityConfig);

		if (cityConfig.isDefault) {
			this.defaultCity = cityConfig;
		}
	}

	getCity(cityId: string): ICityConfig | undefined {
		return this.cities.get(cityId);
	}

	getDefaultCity(): ICityConfig | null {
		return this.defaultCity;
	}

	getAllCities(): ICityConfig[] {
		return Array.from(this.cities.values());
	}

	findCityBySearchTerm(searchTerm: string): ICityConfig | undefined {
		return Array.from(this.cities.values()).find(city =>
			city.isRelatedToSearch(searchTerm)
		);
	}

	findCityByState(state: string): ICityConfig | undefined {
		return Array.from(this.cities.values()).find(
			city => city.state.toUpperCase() === state.toUpperCase()
		);
	}

	hasStaticDataForState(state: string): boolean {
		const city = this.findCityByState(state);
		return city !== undefined && city.addressesData.regions.length > 0;
	}

	/**
	 * Retorna os dados de endereços estáticos que devem ser mesclados
	 * @param fetchedAddresses - Endereços vindos da API
	 */
	getMergedAddresses(fetchedAddresses: Addresses[]): Addresses[] {
		if (fetchedAddresses.length === 0) {
			return fetchedAddresses;
		}

		// Cria um Set para lookup O(1) dos estados que já existem na API
		const existingStates = new Set(fetchedAddresses.map(addr => addr.state));

		// Filtra cidades que precisam ser adicionadas
		const citiesToAdd = this.getAllCities().filter(
			city =>
				city.addressesData.regions.length > 0 && !existingStates.has(city.state)
		);

		// Se não há nada a adicionar, retorna o array original sem criar cópia
		if (citiesToAdd.length === 0) {
			return fetchedAddresses;
		}

		// Só cria novo array se realmente precisar adicionar algo
		return [
			...fetchedAddresses,
			...citiesToAdd.map(city => city.addressesData)
		];
	}

	getAllStates(): string[] {
		return Array.from(new Set(this.getAllCities().map(city => city.state)));
	}

	getStaticSuggestions(searchTerm: string): StaticSuggestions {
		const city = this.findCityBySearchTerm(searchTerm);

		if (city) {
			return city.getStaticSuggestions(searchTerm);
		}

		return {
			regions: [],
			cities: [],
			streets: [],
			pointsOfInterest: [],
			condos: []
		};
	}

	getAllStaticSuggestions(searchTerm: string): StaticSuggestions {
		const allSuggestions: StaticSuggestions = {
			regions: [],
			cities: [],
			streets: [],
			pointsOfInterest: [],
			condos: []
		};

		this.getAllCities().forEach(city => {
			const suggestions = city.getStaticSuggestions(searchTerm);

			allSuggestions.regions.push(...suggestions.regions);
			allSuggestions.cities.push(...suggestions.cities);
			allSuggestions.streets.push(...suggestions.streets);
			allSuggestions.pointsOfInterest.push(...suggestions.pointsOfInterest);
			allSuggestions.condos.push(...suggestions.condos);
		});

		return allSuggestions;
	}

	clear(): void {
		this.cities.clear();
		this.defaultCity = null;
	}
}
